// utils/axiosConfig.js
import axios from 'axios';

let storeRef = null;
export const setupAxiosInterceptors = (store) => {
  storeRef = store;
};

const baseURL =
  process.env.REACT_APP_API_BASE_URL || 'https://api.there2b.com/api';

if (!/^https?:\/\//.test(baseURL)) {
  console.error('BAD API baseURL:', baseURL);
}
// Main API client (has interceptors)
const api = axios.create({
  baseURL,
  withCredentials: true, // needed for httpOnly refresh cookie
});

// Separate client ONLY for refresh/logout (so we don't recurse interceptors)
const authApi = axios.create({
  baseURL,
  withCredentials: true,
});

// ---- REQUEST: attach token (never stale) ----
api.interceptors.request.use(
  (config) => {
    const state = storeRef?.getState?.();
    const token = state?.auth?.token || localStorage.getItem('token'); // fallback during migration

    config.headers = config.headers || {};

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      delete config.headers.Authorization;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ---- RESPONSE: refresh + retry on 401 ----
let isRefreshing = false;
let queue = [];

const flushQueue = (err, newToken = null) => {
  queue.forEach(({ resolve, reject }) => {
    if (err) reject(err);
    else resolve(newToken);
  });
  queue = [];
};

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const status = error?.response?.status;
    const original = error?.config;

    if (!original) return Promise.reject(error);

    const explicitlyLoggedOut = localStorage.getItem('explicit_logout') === '1';
    if (explicitlyLoggedOut) {
      return Promise.reject(error);
    }

    const isAuthEndpoint =
      original.url?.includes('/auth/login') ||
      original.url?.includes('/auth/refresh') ||
      original.url?.includes('/auth/logout') ||
      original.url?.includes('/auth/check');

    if (status === 401 && !isAuthEndpoint && !original._retry) {
      original._retry = true;

      // If refresh already running, wait
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          queue.push({ resolve, reject });
        }).then((newToken) => {
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${newToken}`;
          return api(original);
        });
      }

      isRefreshing = true;

      try {
        const refreshRes = await authApi.get('/auth/refresh');

        const newToken = refreshRes.data?.token || refreshRes.data?.accessToken;
        const user = refreshRes.data?.user;

        if (!newToken) throw new Error('Refresh did not return a token');

        // ✅ Session restored legitimately → clear explicit logout flag
        localStorage.removeItem('explicit_logout');

        // ✅ Keep Redux + localStorage aligned
        storeRef?.dispatch({
          type: 'AUTH_REFRESHED',
          payload: { token: newToken, user },
        });

        flushQueue(null, newToken);

        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      } catch (refreshErr) {
        flushQueue(refreshErr, null);

        // best-effort: clear refresh cookie server-side
        try {
          await authApi.post('/auth/logout');
        } catch (_) {}

        // ✅ triggers your reducer cleanup (localStorage removal etc.)
        storeRef?.dispatch({ type: 'LOGOUT', payload: 'Session expired' });

        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;

// Export authApi too if you want to use it directly in logout/login flows
export { authApi };
